import React, { useState } from 'react';
import {
  BookOpen, ChevronDown, ChevronUp, Rocket, Mic, Play, Copy,
  Settings, Info, Globe, Lock, FileText, Volume2, Zap, Cpu,
  Link, Pause, AlertTriangle,
  RefreshCw, WifiOff, VolumeX, BookMarked
} from 'lucide-react';
import './Instructions.css';

const HostInstructions = () => {
  const [expandedSections, setExpandedSections] = useState({
    quickStart: true,
    settings: false,
    sharing: false,
    advanced: false,
    troubleshooting: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="instructions-container">
      <h2 className="instructions-title"><BookOpen className="icon-inline" size={28} /> Host Guide</h2>

      <div className="instruction-section">
        <div
          className="section-header"
          onClick={() => toggleSection('quickStart')}
        >
          <h3><Rocket className="icon-inline" size={20} /> Quick Start Guide</h3>
          <span className={`collapse-icon ${expandedSections.quickStart ? 'expanded' : ''}`}>
            {expandedSections.quickStart ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </span>
        </div>
        {expandedSections.quickStart && (
          <div className="section-content">
            <ol className="instruction-list">
              <li>
                <strong>Configure Your Session</strong>
                <ul>
                  <li><strong>Session ID:</strong> Enter a unique ID or leave blank for auto-generation</li>
                  <li><strong>Source Language:</strong> Select the language you'll be speaking in</li>
                  <li><strong>Target Languages:</strong> Add one or more languages for translation</li>
                </ul>
              </li>
              <li>
                <strong>Test Your Microphone</strong>
                <p>Click "<Mic className="icon-inline" size={16} /> Test Mic" next to the Source Language dropdown to ensure your microphone is working properly.</p>
              </li>
              <li>
                <strong>Start the Session</strong>
                <p>Click "Start Session" to begin broadcasting. You'll see connection status indicators.</p>
              </li>
              <li>
                <strong>Share with Participants</strong>
                <p>Select a target language from the dropdown, then click "<Copy className="icon-inline" size={16} /> Copy URL" to get a link for your participants.</p>
              </li>
              <li>
                <strong>Begin Speaking</strong>
                <p>Start talking! Your speech will be transcribed and translated in real-time.</p>
              </li>
            </ol>
          </div>
        )}
      </div>

      <div className="instruction-section">
        <div
          className="section-header"
          onClick={() => toggleSection('settings')}
        >
          <h3><Settings className="icon-inline" size={20} /> Session Settings</h3>
          <span className={`collapse-icon ${expandedSections.settings ? 'expanded' : ''}`}>
            {expandedSections.settings ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </span>
        </div>
        {expandedSections.settings && (
          <div className="section-content">
            <div className="settings-grid">
              <div className="setting-item">
                <h4><Info className="icon-inline" size={18} /> Session ID</h4>
                <p>A unique identifier for your session. Auto-generated if left blank.</p>
                <p className="tip"><Info className="icon-inline" size={14} /> Tip: Use memorable IDs like "meeting-2024-jan" for easy sharing.</p>
              </div>

              <div className="setting-item">
                <h4><Mic className="icon-inline" size={18} /> Source Language</h4>
                <p>The language you'll be speaking. Ensures accurate speech recognition.</p>
              </div>

              <div className="setting-item">
                <h4><Globe className="icon-inline" size={18} /> Target Languages</h4>
                <p>Languages to translate into. You can add multiple target languages.</p>
                <p className="tip"><Info className="icon-inline" size={14} /> Tip: Participants choose their preferred language when joining.</p>
              </div>

              <div className="setting-item">
                <h4><Lock className="icon-inline" size={18} /> Session Password</h4>
                <p>Optional password for secure sessions. Required for Zoom integration.</p>
              </div>

              <div className="setting-item">
                <h4><FileText className="icon-inline" size={18} /> Host Instructions</h4>
                <p>Optional context to improve translation accuracy (e.g., "Medical conference about cardiology").</p>
              </div>

              <div className="setting-item">
                <h4><Volume2 className="icon-inline" size={18} /> Text-to-Speech</h4>
                <p>Enable TTS for participants to hear translations spoken aloud.</p>
              </div>

              <div className="setting-item">
                <h4><Zap className="icon-inline" size={18} /> Partial Text</h4>
                <p>Show work-in-progress translations for faster feedback.</p>
              </div>

              <div className="setting-item">
                <h4><Cpu className="icon-inline" size={18} /> Translator & Model Selection</h4>
                <p>Choose your translation service and AI model:</p>
                <ul>
                  <li><strong>OpenAI:</strong> GPT-5.2 ⭐️ (default), GPT-5.1, GPT-4o 🔥, GPT-4o Mini</li>
                  <li><strong>Azure OpenAI:</strong> Same models as OpenAI, hosted on Azure</li>
                  <li><strong>Google Gemini:</strong> Gemini 2.5 Pro ⭐️, Gemini 2.5 Flash, Gemini 2.5 Flash Lite ⚡️, Gemini 2.0 Flash, Gemini 2.0 Flash Lite</li>
                  <li><strong>Google Translate:</strong> Traditional Google Translate API (no model selection)</li>
                  <li><strong>DeepSeek:</strong> DeepSeek Chat, DeepSeek Coder</li>
                  <li><strong>DeepL/Azure AI:</strong> No model selection needed</li>
                </ul>
                <p className="tip"><Info className="icon-inline" size={14} /> Model dropdown appears automatically based on translator choice!</p>

                <h5><Settings className="icon-inline" size={16} /> Custom Model Options</h5>
                <ul>
                  <li><strong>Dropdown Method:</strong> Select "<Settings className="icon-inline" size={14} /> Custom Model" from any AI translator's model list</li>
                  <li><strong>URL Override:</strong> Add <code>?customModel=your-model-name</code> to the page URL</li>
                  <li><strong>Priority:</strong> URL override takes precedence over dropdown selection</li>
                </ul>
                <p className="tip"><Info className="icon-inline" size={14} /> Perfect for testing new models or using deployment-specific model names!</p>
              </div>

              <div className="setting-item">
                <h4><BookMarked className="icon-inline" size={18} /> Glossary Support</h4>
                <p>Use domain-specific glossaries to improve translation accuracy for technical terms:</p>
                <ul>
                  <li><strong>Tenant Glossary:</strong> Organization-specific terminology (requires Tenant ID + Glossary Name)</li>
                  <li><strong>Category Glossary:</strong> Shared domain glossaries (e.g., "medical", "legal", "education-k13")</li>
                  <li><strong>Combined:</strong> Use both tenant and category glossaries together for best results</li>
                  <li><strong>AI Extract:</strong> Upload a document (PDF, Word, etc.) to automatically extract domain terms via AI, review candidates, then publish to your glossary</li>
                  <li><strong>Inline Edit:</strong> Click the edit button next to any glossary entry to update it directly without leaving the glossary view</li>
                </ul>
                <p className="tip"><Info className="icon-inline" size={14} /> Glossaries protect technical terms from mistranslation using placeholder substitution!</p>
              </div>

              <div className="setting-item">
                <h4><Zap className="icon-inline" size={18} /> Unified Model</h4>
                <p>Control translation quality vs. speed trade-off:</p>
                <ul>
                  <li><strong>Enabled (Quality Priority):</strong> Uses same high-quality model for both partial and final translations</li>
                  <li><strong>Disabled (Speed Priority):</strong> May use faster models for partial translations</li>
                </ul>
                <p className="tip"><Info className="icon-inline" size={14} /> Enable for consistent translation quality; disable for faster partial results.</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="instruction-section">
        <div
          className="section-header"
          onClick={() => toggleSection('sharing')}
        >
          <h3><Link className="icon-inline" size={20} /> Sharing Your Session</h3>
          <span className={`collapse-icon ${expandedSections.sharing ? 'expanded' : ''}`}>
            {expandedSections.sharing ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </span>
        </div>
        {expandedSections.sharing && (
          <div className="section-content">
            <div className="sharing-guide">
              <h4>Methods to Share</h4>
              <ol>
                <li>
                  <strong>Direct Link</strong>
                  <p>Select a language from the dropdown, then click "<Copy className="icon-inline" size={14} /> Copy URL" to get a pre-configured link.</p>
                  <code>{`${window.location.origin}/receiver?sessionId=meeting-2024&targetLanguage=es-ES&enableTts=false&showPartial=true&password=mypassword`}</code>
                </li>
                <li>
                  <strong>Manual Entry</strong>
                  <p>Share just the Session ID and let participants configure their own settings.</p>
                </li>
                <li>
                  <strong>QR Code</strong>
                  <p>Generate a QR code from the receiver URL for easy mobile access.</p>
                </li>
              </ol>

              <h4>Best Practices</h4>
              <ul>
                <li>Share the link before starting your presentation</li>
                <li>Include the session password if required</li>
                <li>Test the link yourself in an incognito window</li>
                <li>Have a backup plan for sharing (email, chat, etc.)</li>
                <li>Inform mobile users they can hide controls for maximum viewing area</li>
                <li>Tell participants all settings are pre-configured - they just need to join!</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      <div className="instruction-section">
        <div
          className="section-header"
          onClick={() => toggleSection('advanced')}
        >
          <h3><Rocket className="icon-inline" size={20} /> Advanced Features</h3>
          <span className={`collapse-icon ${expandedSections.advanced ? 'expanded' : ''}`}>
            {expandedSections.advanced ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </span>
        </div>
        {expandedSections.advanced && (
          <div className="section-content">
            <div className="advanced-features">
              <div className="feature-item">
                <h4><Pause className="icon-inline" size={18} /> Pause/Resume</h4>
                <p>Temporarily pause translations during breaks without ending the session.</p>
                <ul>
                  <li>Click "<Pause className="icon-inline" size={14} /> Pause Session" to stop broadcasting</li>
                  <li>Participants see a "Session Paused" message</li>
                  <li>Click "<Play className="icon-inline" size={14} /> Resume Session" to continue</li>
                </ul>
              </div>

              <div className="feature-item">
                <h4><Zap className="icon-inline" size={18} /> Semantic Segmentation</h4>
                <p>Enable for better sentence boundary detection in continuous speech.</p>
              </div>

              <div className="feature-item">
                <h4><Settings className="icon-inline" size={18} /> Confidence Threshold</h4>
                <p>Set minimum confidence level (0.0-1.0) for speech recognition accuracy.</p>
                <ul>
                  <li>0.0 = Accept all recognitions</li>
                  <li>0.5 = Moderate filtering</li>
                  <li>1.0 = Only perfect confidence</li>
                </ul>
              </div>

              <div className="feature-item">
                <h4><RefreshCw className="icon-inline" size={18} /> Live Translation Preview</h4>
                <p>Monitor translations in real-time to ensure accuracy.</p>
              </div>

              <div className="feature-item">
                <h4><Mic className="icon-inline" size={18} /> Voice Type Selection</h4>
                <p>Choose male or female voice for TTS playback.</p>
              </div>

              <div className="feature-item">
                <h4><Settings className="icon-inline" size={18} /> Custom Model Selection</h4>
                <p>Use specific AI models not in the standard dropdown:</p>
                <ul>
                  <li><strong>Via Dropdown:</strong> Select "<Settings className="icon-inline" size={14} /> Custom Model", then enter model name</li>
                  <li><strong>Via URL:</strong> Add <code>?customModel=gpt-4-turbo-preview</code> to page URL</li>
                  <li><strong>Override Indicator:</strong> Blue banner shows when URL override is active</li>
                  <li><strong>Clear Override:</strong> Click "Clear Override" button to return to dropdown</li>
                </ul>
                <p className="tip"><Info className="icon-inline" size={14} /> Great for testing new models or using specific deployment names!</p>
              </div>

              <div className="feature-item">
                <h4><Mic className="icon-inline" size={18} /> Google Speech Recognition Models</h4>
                <p>When using Google transcriber, choose the optimal speech recognition model for your use case:</p>
                <ul>
                  <li><strong>Auto-select (Default):</strong> Google automatically chooses the best model</li>
                  <li><strong>latest_long:</strong> 📈 Best for meetings, conferences, podcasts - highest accuracy, higher latency</li>
                  <li><strong>latest_short:</strong> ⚡ Best for short queries and commands - balanced performance</li>
                  <li><strong>phone_call:</strong> 📞 Optimized for phone/video calls with compressed audio</li>
                  <li><strong>command_and_search:</strong> <Rocket className="icon-inline" size={14} /> Best for voice commands and real-time interaction - lowest latency</li>
                </ul>
                <p className="tip"><Info className="icon-inline" size={14} /> Choose based on your meeting type: formal presentations use latest_long, interactive sessions use command_and_search!</p>
                <p><strong>How to Select:</strong> This option appears in Speech Recognition Settings when Google transcriber is selected.</p>
              </div>

              <div className="feature-item">
                <h4><Zap className="icon-inline" size={18} /> Single Utterance Mode (Google STT)</h4>
                <p>When using Google transcriber, enable Single Utterance Mode for faster, shorter STT results:</p>
                <ul>
                  <li><strong>Faster Results:</strong> Google STT finalizes transcripts more aggressively after detecting speech pauses</li>
                  <li><strong>Shorter Segments:</strong> Get quicker feedback instead of waiting for longer, complete sentences</li>
                  <li><strong>Stream Management:</strong> May close and restart STT streams more frequently for optimal performance</li>
                  <li><strong>Best For:</strong> Interactive sessions where quick response time is more important than complete sentences</li>
                </ul>
                <p className="tip"><Info className="icon-inline" size={14} /> This is an advanced feature that trades some stream stability for faster response times!</p>
                <p><strong>How to Enable:</strong> Check "Enable Single Utterance Mode (Faster Results)" in Google Speech Recognition Settings.</p>
              </div>

              <div className="feature-item">
                <h4><Globe className="icon-inline" size={18} /> URL Parameter Configuration</h4>
                <p>Pre-configure sessions using URL parameters for power users:</p>
                <ul>
                  <li><code>?translator=OpenAI&model=gpt-5.1</code></li>
                  <li><code>?customModel=gpt-4-turbo-preview</code></li>
                  <li><code>?enableTts=true&voiceType=female</code></li>
                  <li><code>?targetLanguages=es-ES,fr-FR,de-DE</code></li>
                  <li><code>?transcriber=Google&googleSttModel=latest_long</code></li>
                  <li><code>?transcriber=Google&singleUtterance=true</code></li>
                  <li><code>?canUnifyModel=true</code> (quality priority)</li>
                  <li><code>?tenantId=org-123&glossaryName=medical-terms</code></li>
                  <li><code>?categoryKey=medical</code> (shared glossary)</li>
                  <li><code>?interpreterMode=true&segmentationTimeout=150</code></li>
                  <li><code>?confidenceThreshold=0.5</code></li>
                </ul>
                <p className="tip"><Info className="icon-inline" size={14} /> Perfect for creating templates or automating session setup!</p>
              </div>

              <div className="feature-item">
                <h4><BookMarked className="icon-inline" size={18} /> Glossary Configuration</h4>
                <p>Configure glossaries via URL parameters:</p>
                <ul>
                  <li><strong>Tenant Glossary:</strong> <code>?tenantId=org-123&glossaryName=my-glossary</code></li>
                  <li><strong>Category Glossary:</strong> <code>?categoryKey=medical</code> or <code>?categoryKey=legal</code></li>
                  <li><strong>Both Combined:</strong> <code>?tenantId=org-123&glossaryName=custom&categoryKey=medical</code></li>
                </ul>
                <p className="tip"><Info className="icon-inline" size={14} /> Glossary entries are automatically loaded and applied during translation!</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="instruction-section">
        <div
          className="section-header"
          onClick={() => toggleSection('troubleshooting')}
        >
          <h3><Settings className="icon-inline" size={20} /> Troubleshooting</h3>
          <span className={`collapse-icon ${expandedSections.troubleshooting ? 'expanded' : ''}`}>
            {expandedSections.troubleshooting ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </span>
        </div>
        {expandedSections.troubleshooting && (
          <div className="section-content">
            <div className="troubleshooting-list">
              <div className="trouble-item">
                <h4><Mic className="icon-inline" size={18} /> Microphone Issues</h4>
                <ul>
                  <li>Grant microphone permission when prompted</li>
                  <li>Check if other apps are using the microphone</li>
                  <li>Try a different browser</li>
                  <li>Test with "<Mic className="icon-inline" size={14} /> Test Mic" button</li>
                  <li>Check system audio settings</li>
                </ul>
              </div>

              <div className="trouble-item">
                <h4><WifiOff className="icon-inline" size={18} /> Connection Problems</h4>
                <ul>
                  <li>Check internet connection stability</li>
                  <li>Verify API keys are valid</li>
                  <li>Look for error messages in the status banner</li>
                  <li>Try refreshing the page</li>
                  <li>Check if session already exists with same ID</li>
                </ul>
              </div>

              <div className="trouble-item">
                <h4><VolumeX className="icon-inline" size={18} /> No Translations Appearing</h4>
                <ul>
                  <li>Ensure you're speaking clearly near the microphone</li>
                  <li>Check if session is paused</li>
                  <li>Verify target languages are selected</li>
                  <li>Look for WebSocket connection status (should be green)</li>
                  <li>Check translation service status indicator</li>
                </ul>
              </div>

              <div className="trouble-item">
                <h4><AlertTriangle className="icon-inline" size={18} /> Poor Translation Quality</h4>
                <ul>
                  <li>Speak in complete sentences</li>
                  <li>Pause briefly between sentences</li>
                  <li>Add context in Host Instructions field</li>
                  <li>Try a different AI model</li>
                  <li>Reduce background noise</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="instructions-footer">
        <div className="pro-tips">
          <h4><Info className="icon-inline" size={18} /> Pro Tips</h4>
          <ul>
            <li>Test your setup before the actual event</li>
            <li>Use a good quality microphone for best results</li>
            <li>Speak at a moderate pace with clear enunciation</li>
            <li>Monitor the live translation preview to catch any issues</li>
            <li>Have a co-host monitor the participant experience</li>
            <li><strong>Settings Access:</strong> Click "Show Settings" / "Hide Settings" button to toggle the configuration form</li>
            <li><strong>Session Controls:</strong> When settings are collapsed, all essential controls remain visible for quick access</li>
            <li><strong>Stop Button:</strong> The red Stop Session button permanently ends the session - use Pause for temporary breaks</li>
            <li><strong>Mobile Users:</strong> Inform participants they can hide controls after joining for maximum translation viewing area</li>
            <li><strong>Shared Links:</strong> Pre-configured links provide the smoothest participant experience - no setup required!</li>
            <li><strong>Google Services:</strong> Choose "Google Gemini" for AI-powered translation or "Google Translate" for traditional API</li>
            <li><strong>Model Testing:</strong> Use custom model selection to test new AI models before they're added to the dropdown</li>
            <li><strong>URL Templates:</strong> Create bookmark templates with URL parameters for quick session setup</li>
            <li><strong>Custom Override:</strong> Blue URL override banner shows when a custom model is active via URL parameter</li>
            <li><strong>Glossary Usage:</strong> Create glossaries for domain-specific terminology to improve translation accuracy</li>
            <li><strong>Unified Model:</strong> Enable "Unified Model" for consistent quality across partial and final translations</li>
            <li><strong>Interpreter Mode:</strong> Use interpreter mode with shorter timeouts (150ms) for real-time interpretation</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default HostInstructions;
